import { FC } from 'react';
import { useTranslation, Trans } from 'next-i18next';
import classNames from 'classnames';
import type { IconIds } from 'src/common/components/IconDS22';
import {
  SHARED_MEDIA_QUERIES as sharedMediaQueries,
  GMB_MEDIA_EMAIL, NEXT_STATIC_IMAGES, NUMBER_OF_LISTINGS,
} from 'src/common/constants';
import getClassNames from 'src/common/utils/getClassNames';
import Icon from 'src/common/components/IconDS22';
import Typography from 'src/common/components/Typography';
import UniversalLink from 'src/common/components/UniversalLink';
import { buildSrcSet } from 'src/common/utils/imageHelpers';
import styles from './HeroGridCard.module.scss';

type CardImageSizes = {
  'default': number; 's'?: number; 'm'?: number; 'l'?: number; 'xl'?: number;
};

type CardImage = {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  sizes?: CardImageSizes;
};

export type HeroGridCardProps = {
  id: number;
  cardType: 'details-card' | 'logo-card' | 'image';
  cardBackground?: 'bluminescence-02' | 'midnight-01' | 'midnight-02';
  image?: CardImage;
  iconId?: IconIds;
  iconColor?: 'sunset' | 'sand' | 'bluminescence-01' | 'midnight-01';
  title?: string;
  text?: string;
  linkText?: string;
  linkHref?: string;
  linkIconId?: IconIds;
  visibleFrom?: 'xl';
  gridColumnSpanStart?: 'm' | 'xl';
  gridColumnSpanEnd?: 'xl';
};

const DEFAULT_IMAGE_SIZE = 'default';

const buildImageUrl = (cardImage: CardImage, size: keyof CardImageSizes) => {
  if (typeof cardImage.sizes?.[size] === 'number') {
    const src = `${NEXT_STATIC_IMAGES}about/cards/${cardImage.src}--${size}.jpg`;
    return buildSrcSet(src, { w: cardImage.sizes[size] });
  }
  return undefined;
};

const HeroGridCard: FC<HeroGridCardProps> = ({
  id,
  cardType,
  cardBackground,
  image,
  iconId,
  iconColor,
  title,
  text,
  linkText,
  linkHref,
  linkIconId,
  visibleFrom,
  gridColumnSpanStart,
  gridColumnSpanEnd,
}) => {
  const [t] = useTranslation('about-page');
  const isDetailsCard = cardType === 'details-card';
  const isLogoCard = cardType === 'logo-card';
  const isImageCard = cardType === 'image';

  return (
    <li
      className={getClassNames(styles, 'root', classNames(cardBackground, `card--${id}`, {
        isDetailsCard,
        isLogoCard,
        isImageCard,
        isWithoutIcon: !iconId,
        isWithoutTitle: !title,
        isVisibleFromXL: visibleFrom === 'xl',
        gridColumnSpanStartMed: gridColumnSpanStart === 'm',
        gridColumnSpanStartXl: gridColumnSpanStart === 'xl',
        gridColumnSpanEndXl: gridColumnSpanEnd === 'xl',
      }))}
    >
      {isLogoCard && image && (
        /* eslint-disable-next-line @next/next/no-img-element */
        <img
          src={`${NEXT_STATIC_IMAGES}${image.src}`}
          width={image.width}
          height={image.height}
          alt={t(`${image.alt}`)}
          loading="lazy"
        />
      )}
      {iconId && (
        <div className={getClassNames(styles, 'icon', classNames(iconColor))}>
          <Icon id={iconId} size="xl" />
        </div>
      )}
      {title && (
        <div className={styles.title}>
          <Typography type="h2" size="heading-5" weight="semibold">
            {t(`${title}`)}
          </Typography>
        </div>
      )}
      {text && (
        <div className={styles.text}>
          <Typography type="p" size="body-l">
            <Trans
              t={t}
              i18nKey={(`${text}`)}
              components={{
                break: <br />,
                mediaEmailLink: (
                  <UniversalLink
                    href={`mailto:${GMB_MEDIA_EMAIL}`}
                    openInNewTab
                  />
                ),
              }}
              values={{
                mediaEmail: GMB_MEDIA_EMAIL,
                numberOfListings: NUMBER_OF_LISTINGS.toLocaleString(),
              }}
            />
          </Typography>
        </div>
      )}
      {linkText && linkHref && (
        <div className={styles.link}>
          <UniversalLink
            href={linkHref}
            openInNewTab
          >
            <Typography type="p" size="body-l" weight="semibold">
              <span className={styles.linkText}>
                {t(`${linkText}`)}
                {linkIconId && <Icon id={linkIconId} size="m" className={styles.linkIcon} />}
              </span>
            </Typography>
          </UniversalLink>
        </div>
      )}
      {isImageCard && image?.sizes && (
        <picture>
          {Object.keys(image.sizes).map((size: keyof CardImageSizes) => {
            if (size !== DEFAULT_IMAGE_SIZE) {
              return (
                <source
                  key={size}
                  media={sharedMediaQueries[size]}
                  srcSet={buildImageUrl(image, size)}
                />
              );
            }
            return null;
          })}
          <img
            srcSet={buildImageUrl(image, DEFAULT_IMAGE_SIZE)}
            alt={t(`${image.alt}`)}
            loading={id === 1 ? 'eager' : 'lazy'}
          />
        </picture>
      )}
    </li>
  );
};

export default HeroGridCard;
